import { useState } from 'react';
import {
  PortalDsAlert,
  PortalDsButton,
  PortalDsCard,
  PortalDsSectionHeader,
  portalDs,
} from 'src/components/portal-design-system';
import { useClearPerformanceCacheMutation } from 'src/redux/operations/operations.endpoints';

function getErrorMessage(error: unknown): string {
  return (error as { message?: string; data?: { status?: { status_message?: string } } })?.data
    ?.status?.status_message
    ?? (error as { message?: string })?.message
    ?? 'Could not clear the performance cache.';
}

export function SettingsCachesSection() {
  const [clearCache, clearState] = useClearPerformanceCacheMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleClear = async () => {
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      const result = await clearCache().unwrap();
      const deletedTotal = result.deleted_total ?? 0;
      setSuccessMessage(
        deletedTotal === 1
          ? 'Cleared 1 cached performance snapshot. The next Performance page load will fetch fresh data.'
          : `Cleared ${deletedTotal} cached performance snapshots. The next Performance page load will fetch fresh data.`,
      );
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  return (
    <PortalDsCard aria-labelledby="settings-caches-heading">
      <PortalDsSectionHeader
        overline="Caches"
        title="Performance"
        titleId="settings-caches-heading"
        description="Clears Redis snapshots used by the Performance overview so the next visit recomputes from the warehouse."
        actions={(
          <PortalDsButton
            type="button"
            variant="accent"
            size="sm"
            onClick={() => void handleClear()}
            disabled={clearState.isLoading}
          >
            {clearState.isLoading ? 'Clearing…' : 'Clear cache'}
          </PortalDsButton>
        )}
      />

      <p className={portalDs.bodySm}>
        Use this after ETL or data fixes when the overview still shows stale numbers.
        Aggregate, time-series, and account-filter caches are all removed.
      </p>

      {errorMessage ? (
        <div className="mt-5">
          <PortalDsAlert variant="danger" title="Could not clear cache">
            {errorMessage}
          </PortalDsAlert>
        </div>
      ) : null}

      {successMessage ? (
        <div className="mt-5">
          <PortalDsAlert variant="success" title="Cache cleared">
            {successMessage}
          </PortalDsAlert>
        </div>
      ) : null}
    </PortalDsCard>
  );
}
